import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  ChangeEvent,
} from "react";
import { useLazyGetEndUsersQuery } from "services/endUserApi";
import { EndUsersResponse, ListEndUserOptions } from "services/endUserApi";
import { Order, User } from "helpers";
import { FilterItem } from "components/Table";
import { useUser } from "hooks";

interface UserParams {
  search?: string;
  sort?: "full_name" | "city" | "email" | "phone_number" | "last_sign_in";
  order?: Order;
  status?: string;
  confirmation_status?: boolean;
  client_state?: "GUEST_MODE" | "REGISTERED_MODE";
  city_id?: number | undefined;
}

const initialUserParams: UserParams = {
  search: undefined,
  sort: "full_name",
  order: "DESC",
  status: undefined,
  confirmation_status: undefined,
  client_state: undefined,
  city_id: undefined,
};

export interface UserManagementContextValues {
  user: User | undefined;
  data: EndUsersResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  params: UserParams;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  refetch: () => void;
  onSort: (sort: "full_name" | "city" | "email" | "phone_number" | "last_sign_in", order?: Order) => void;
  onSearch: (search: string) => void;
  onFilterByStatus: (status?: FilterItem) => void;
  onResetFilters: () => void;
  onChangeCity: (event: { value: string; label: string } | ChangeEvent<HTMLSelectElement>) => void;
}

export const UserManagementContext = createContext<UserManagementContextValues | undefined>(undefined);

export const UserManagementProvider = ({ children }: { children: ReactNode }) => {
  const [params, setParams] = useState<UserParams>(initialUserParams);
  const [page, setPage] = useState<number>(0);
  const pageSize = 10;
  const { user } = useUser();

  const [trigger, result] = useLazyGetEndUsersQuery();

  useEffect(() => {
    console.log("Fetching with params:", params); // Debug
    onFetchHandler(true);
  }, [params, page]);

  const onFetchHandler = async (preferCacheValue: boolean = false) => {
    try {
      await trigger({ page, pageSize, ...params }, preferCacheValue);
    } catch (error) {
      console.error("Error fetching end users:", error);
    }
  };

  const onSortHandler = (sort: "full_name" | "city" | "email" | "phone_number" | "last_sign_in", order?: Order) => {
    setPage(0);
    setParams((oldParams) => {
      let newOrder: Order = "ASC";
      if (oldParams.sort === sort && oldParams.order) {
        newOrder = oldParams.order === "ASC" ? "DESC" : "ASC";
      } else if (order) {
        newOrder = order;
      }
      return { ...oldParams, sort, order: newOrder };
    });
  };

  const onSearchHandler = (search: string) => {
    setPage(0);
    setParams((oldParams) => ({ ...oldParams, search }));
  };

  const onFilterByStatusHandler = (status?: FilterItem) => {
    setPage(0);
    setParams((oldParams) => ({
      ...oldParams,
      status: status ? String(status) : undefined,
      client_state: status ? (String(status) === "0" ? "GUEST_MODE" : "REGISTERED_MODE") : undefined, // Fix status mapping
    }));
  };

  const onChangeCityHandler = (event: { value: string; label: string } | ChangeEvent<HTMLSelectElement>) => {
    setPage(0);
    const city_id = "target" in event ? event.target.value : event.value;
    setParams((oldParams) => ({
      ...oldParams,
      city_id: Number(city_id) || undefined,
    }));
  };

  const onResetFiltersHandler = () => {
    setPage(0);
    setParams(initialUserParams);
  };

  const values: UserManagementContextValues = {
    user,
    data: result?.data,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    isSuccess: result.isSuccess,
    params,
    page,
    setPage,
    refetch: () => onFetchHandler(false),
    onSort: onSortHandler,
    onSearch: onSearchHandler,
    onFilterByStatus: onFilterByStatusHandler,
    onResetFilters: onResetFiltersHandler,
    onChangeCity: onChangeCityHandler,
  };

  return <UserManagementContext.Provider value={values}>{children}</UserManagementContext.Provider>;
};

export interface UserManagementContextValues {
  user: User | undefined;
  data: EndUsersResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  params: UserParams;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  refetch: () => void;
  onSort: (sort: "full_name" | "city" | "email" | "phone_number" | "last_sign_in", order?: Order) => void;
  onSearch: (search: string) => void;
  onFilterByStatus: (status?: FilterItem) => void;
  onResetFilters: () => void;
  onChangeCity: (event: { value: string; label: string } | ChangeEvent<HTMLSelectElement>) => void;
}

export const useUserManagementContext = () => {
  const context = useContext(UserManagementContext);
  if (!context) {
    throw new Error("useUserManagement must be used within a UserManagementProvider!");
  }
  return context;
};
